import { useLingui } from '@lingui/react/macro';

import ArrowNEIcon from '@watershed/icons/components/ArrowNE';
import CheckmarkIcon from '@watershed/icons/components/Checkmark';
import InfoIcon from '@watershed/icons/components/Info';
import LockKeyholeIcon from '@watershed/icons/components/LockKeyhole';
import MoreIcon from '@watershed/icons/components/More';
import Icon from '@watershed/icons/Icon';
import { routeForLearningHub } from '@watershed/shared-universal/dashboardRoutes';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import DropdownMenu from '@watershed/ui-core/components/DropdownMenu';

// Create a proper component for the default icon
const EmptyIcon = () => <Icon viewBox="0 0 24 24" fill="currentColor" />;

export function AuthMethodDropdown({
  selectedAuthMethod,
  setSelectedAuthMethod,
  hasOAuthClients,
  hasApiKeys,
}: {
  selectedAuthMethod: 'token' | 'oauth';
  setSelectedAuthMethod: (method: 'token' | 'oauth') => void;
  hasOAuthClients: boolean;
  hasApiKeys: boolean;
}) {
  const { t } = useLingui();

  const handleAuthMethodChange = async (method: 'token' | 'oauth') => {
    if (method === selectedAuthMethod) {
      return;
    }
    setSelectedAuthMethod(method);
  };

  const groupLabel = t({
    message: 'Authentication method',
    context: 'Group label for authentication method dropdown',
  });

  const disableTokenOption = selectedAuthMethod === 'oauth' && hasOAuthClients;
  const disableOAuthOption = selectedAuthMethod === 'token' && hasApiKeys;

  const authMethodOptions = [
    {
      groupLabel,
      id: 'token',
      testId: TestIds.ApiAuthMethodDropdownTokenTab,
      label: t({
        message: 'Token authentication',
        context: 'Authentication method option',
      }),
      sublabel: t({
        message: 'Manually generate long-lived API keys',
        context: 'Description for token authentication method',
      }),
      disabled: disableTokenOption,
      tooltip: disableTokenOption
        ? t({
            message:
              'To switch to long-lived API keys, all existing OAuth clients must be removed. This authentication method is less secure and not typically recommended.',
            context: 'Tooltip for disabled token authentication option',
          })
        : undefined,
      tooltipProps: {
        placement: 'left' as const,
      },
      Icon:
        selectedAuthMethod === 'token'
          ? CheckmarkIcon
          : disableTokenOption
            ? LockKeyholeIcon
            : EmptyIcon,
      onSelect: () => handleAuthMethodChange('token'),
    },
    {
      groupLabel,
      id: 'oauth',
      testId: TestIds.ApiAuthMethodDropdownOAuthTab,
      label: t({
        message: 'OAuth 2.0',
        context: 'Authentication method option',
      }),
      sublabel: t({
        message: 'Programmatically generate short-lived API keys',
        context: 'Description for OAuth authentication method',
      }),
      disabled: disableOAuthOption,
      tooltip: disableOAuthOption
        ? t({
            message:
              'To switch to OAuth 2.0 authentication, all existing API keys must be removed.',
            context: 'Tooltip for disabled OAuth 2.0 authentication option',
          })
        : undefined,
      tooltipProps: {
        placement: 'left' as const,
      },
      Icon:
        selectedAuthMethod === 'oauth'
          ? CheckmarkIcon
          : disableOAuthOption
            ? LockKeyholeIcon
            : EmptyIcon,
      onSelect: () => handleAuthMethodChange('oauth'),
    },
  ];
  return (
    <DropdownMenu
      items={[
        ...authMethodOptions,
        {
          id: 'learn-more',
          label: t({
            message: 'Learn more about API keys',
            context: 'Learn more about authentication methods',
          }),
          onSelect: () => {
            window.open(
              routeForLearningHub({
                from: 'OrganizationSettings',
                article: 'Api',
              })
            );
          },
          Icon: InfoIcon,
          EndIcon: ArrowNEIcon,
          menuItemSx: {
            justifyContent: 'flex-start',
            color: 'text.secondary',
            borderTop: (theme) => `thin solid ${theme.palette.grey20}`,
            paddingY: 1,
            backgroundColor: (theme) => theme.palette.grey05,
          },
        },
      ]}
      triggerEndIcon={<MoreIcon />}
      tooltip={t({
        message: 'Authentication method options',
        context: 'Tooltip for authentication method dropdown',
      })}
      groupLayout="inline"
      tooltipStyle="dark"
      testId={TestIds.ApiAuthMethodDropdown}
      buttonProps={{
        variant: 'outlined',
        'aria-label': t({
          message: 'Authentication method options',
          context: 'Aria label for authentication method button',
        }),
      }}
    />
  );
}
